import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, Circle, Loader2, RefreshCw, MessageSquare, X, ExternalLink, ChevronDown, ChevronUp, Send, FileIcon, Download, Calendar, GitBranch, Eye, Edit, Trash2 } from 'lucide-react';
import { taskApi, subTaskApi, employeeApi, positionApi, scheduleApi, getProfileImageUrl, getUploadedFileUrl } from '../services/api';
import { MyTask, Task, TaskConversation, SubTask, SubTaskConversation, Employee, Position, Schedule } from '../types';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { formatCompanyDate, formatCompanyDateTime } from '../utils/companyDateFormat';
import { appConfirm } from '../context/SystemDialogContext';
import {
  filterEmployeesByPositionAssignment,
  filterEmployeesByScheduleAssignment,
} from '../utils/taskAssignmentEmployees';
import AssignmentCircleLink from '../components/AssignmentCircleLink';

/** Task or SubTask — same assignment shape for position/schedule resolution */
type PositionAssignmentSource = {
  assignedToPositions?: Array<{ id: number; title: string }>;
  assignedToPositionIds?: number[];
};

type ScheduleAssignmentSource = {
  assignedToSchedules?: Array<{ id: number; name: string }>;
  assignedToScheduleIds?: number[];
};

function resolveAssignedPositions(
  task: PositionAssignmentSource,
  positions: Pick<Position, 'id' | 'title'>[]
): { id: number; title: string }[] {
  if (task.assignedToPositions && task.assignedToPositions.length > 0) {
    return task.assignedToPositions;
  }
  return (task.assignedToPositionIds || []).map((id) => ({
    id,
    title: positions.find((p) => p.id === id)?.title || `Position #${id}`,
  }));
}

function resolveAssignedSchedules(
  task: ScheduleAssignmentSource,
  schedules: Pick<Schedule, 'id' | 'name'>[]
): { id: number; name: string }[] {
  if (task.assignedToSchedules && task.assignedToSchedules.length > 0) {
    return task.assignedToSchedules;
  }
  return (task.assignedToScheduleIds || []).map((id) => ({
    id,
    name: schedules.find((s) => s.id === id)?.name || `Schedule #${id}`,
  }));
}

/** Why this task appears in My Tasks (from GET /tasks/my-tasks accessLevel). */
const MY_TASK_ACCESS_META: Record<
  MyTask['accessLevel'],
  { label: string; detail: string }
> = {
  creator: {
    label: 'You created this task',
    detail:
      'You are the creator. The task is listed because Created by is you, even if you are not under Assigned to.',
  },
  assigned_to_task: {
    label: 'Assigned to you (task)',
    detail: 'You are directly assigned to this parent task.',
  },
  assigned_to_subtask_only: {
    label: 'Assigned to Sub-Tasks',
    detail: '',
  },
  position_derived: {
    label: 'Position or schedule match',
    detail: '', // Overridden by myTaskAccessMeta using API fields
  },
};

function formatPositionDerivedMatchLine(match: NonNullable<MyTask['positionDerivedMatches']>[number]): string {
  const kind = match.type === 'schedule' ? 'Schedule' : 'Position';
  const scope =
    match.scope === 'subtask' && match.subTaskOrder != null && match.subTaskOrder > 0
      ? ` (Sub-task ${match.subTaskOrder})`
      : match.scope === 'task'
        ? ' (Task)'
        : '';
  const name = (match.name ?? '').trim() || '—';
  return `${kind}: ${name}${scope}`;
}

function formatPositionDerivedAccessMeta(task: MyTask): { label: string; detail: string } {
  const matches = task.positionDerivedMatches ?? [];
  if (matches.length > 0) {
    return {
      label: matches.map(formatPositionDerivedMatchLine).join(' · '),
      detail:
        'You see this task because your profile position or schedule matches an assignment on the task or a sub-task. Sub-task numbers match the SubTasks list order (newest first, then by id). Creator and direct assignment paths are not filtered by location.',
    };
  }

  const atTask = task.positionMatchAtTaskLevel === true;
  const orders = task.positionMatchSubTaskOrders ?? [];
  let label: string;
  if (atTask && orders.length === 0) {
    label = 'Position or schedule match for Task';
  } else if (!atTask && orders.length === 1) {
    label = `Position or schedule match for Sub-task ${orders[0]}`;
  } else if (!atTask && orders.length > 1) {
    label = `Position or schedule match for Sub-tasks ${orders.join(', ')}`;
  } else if (atTask && orders.length === 1) {
    label = `Position or schedule match for Task and Sub-task ${orders[0]}`;
  } else if (atTask && orders.length > 1) {
    label = `Position or schedule match for Task and Sub-tasks ${orders.join(', ')}`;
  } else {
    label = 'Position or schedule match';
  }
  return {
    label,
    detail:
      'Sub-task numbers match the SubTasks list order (newest first, then by id). For this visibility path only, your main/sub locations must include the task location when the task has a location. Creator and direct assignment paths are not filtered by location.',
  };
}

function formatSubTaskOnlyAccessMeta(task: MyTask): { label: string; detail: string } {
  const idSet = new Set(task.assignedSubTaskIds ?? []);
  const namesFromApi = (task.assignedSubTaskNames ?? [])
    .map(n => (n ?? '').trim())
    .filter(n => n.length > 0);
  const namesFromSubs = (task.subTasks ?? [])
    .filter(st => idSet.size === 0 || idSet.has(st.id))
    .map(st => (st.name ?? '').trim())
    .filter(n => n.length > 0);
  const names = namesFromApi.length > 0 ? namesFromApi : namesFromSubs;
  const suffix =
    names.length > 0
      ? names.join(', ')
      : task.assignedSubTaskIds && task.assignedSubTaskIds.length > 0
        ? task.assignedSubTaskIds.map(id => `#${id}`).join(', ')
        : '';

  return {
    label: suffix ? `Assigned to Sub-Tasks ${suffix}` : 'Assigned to Sub-Tasks',
    detail:
      'You are directly assigned to these sub-tasks. The parent task may show no employees under Assigned to; open Sub-tasks for details.',
  };
}

function myTaskAccessMeta(task: MyTask) {
  const level = task.accessLevel;
  if (level === 'position_derived') return formatPositionDerivedAccessMeta(task);
  if (level === 'assigned_to_subtask_only') return formatSubTaskOnlyAccessMeta(task);
  if (level && MY_TASK_ACCESS_META[level]) return MY_TASK_ACCESS_META[level];
  return {
    label: 'Visible to you',
    detail: 'This task is included in your My Tasks list.',
  };
}

/** Edit on a sub-task row (opens Task Admin): not for position-derived-only; otherwise parent assignee/creator or assignee to this sub-task. */
function canEditSubTaskRowInMyTasks(task: MyTask, subTaskId: number): boolean {
  if (task.accessLevel === 'position_derived') return false;
  if (task.accessLevel === 'creator' || task.accessLevel === 'assigned_to_task') return true;
  if (task.accessLevel === 'assigned_to_subtask_only')
    return (task.assignedSubTaskIds ?? []).includes(subTaskId);
  return false;
}

/** Chat on expanded sub-task: same as row edit, plus position-derived viewers may participate. */
function canChatOnSubTaskInMyTasks(task: MyTask, subTaskId: number): boolean {
  if (task.accessLevel === 'position_derived') return true;
  return canEditSubTaskRowInMyTasks(task, subTaskId);
}

const MyTasks: React.FC = () => {
  const { employee } = useAuth();
  const { hasTaskManagement, hasSuperAdminRole } = usePermissions();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<MyTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTask, setSelectedTask] = useState<MyTask | null>(null);
  const [taskDetail, setTaskDetail] = useState<Task | null>(null);
  const [conversations, setConversations] = useState<TaskConversation[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [expandedSubTaskId, setExpandedSubTaskId] = useState<number | null>(null);
  const [subTaskDetailLoading, setSubTaskDetailLoading] = useState<Record<number, boolean>>({});
  const [subTaskDetails, setSubTaskDetails] = useState<Record<number, SubTask>>({});
  const [subTaskConversations, setSubTaskConversations] = useState<Record<number, SubTaskConversation[]>>({});
  const [subTaskNewMessage, setSubTaskNewMessage] = useState<Record<number, string>>({});
  const [sendingSubTaskMessage, setSendingSubTaskMessage] = useState<Record<number, boolean>>({});
  const [positions, setPositions] = useState<Pick<Position, 'id' | 'title'>[]>([]);
  const [schedules, setSchedules] = useState<Pick<Schedule, 'id' | 'name'>[]>([]);
  const [employeesCache, setEmployeesCache] = useState<Employee[]>([]);
  const [assignmentEmployeesModal, setAssignmentEmployeesModal] = useState<{
    kind: 'position' | 'schedule';
    id: number;
    label: string;
    employees: Employee[];
    loading: boolean;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [pos, sched] = await Promise.all([positionApi.getAll(), scheduleApi.getAll()]);
        if (cancelled) return;
        setPositions((pos || []).filter((p: Position) => p.isActive).map((p: Position) => ({ id: p.id, title: p.title })));
        setSchedules((sched || []).filter((s: Schedule) => s.isActive).map((s: Schedule) => ({ id: s.id, name: s.name })));
      } catch {
        if (!cancelled) {
          setPositions([]);
          setSchedules([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const openAssignmentEmployeesModal = useCallback(
    async (kind: 'position' | 'schedule', id: number, label: string, taskLocationId?: number | null) => {
      setAssignmentEmployeesModal({ kind, id, label, employees: [], loading: true });
      try {
        let list: Employee[] = employeesCache.filter((e) => e.isActive);
        if (list.length === 0) {
          const data = await employeeApi.getAll();
          list = (data || []).filter((e: Employee) => e.isActive);
          setEmployeesCache(list);
        }
        const filtered =
          kind === 'position'
            ? filterEmployeesByPositionAssignment(list, id, taskLocationId)
            : filterEmployeesByScheduleAssignment(list, id, taskLocationId);
        setAssignmentEmployeesModal((prev) => {
          if (!prev || prev.kind !== kind || prev.id !== id) return prev;
          return { ...prev, employees: filtered, loading: false };
        });
      } catch {
        setAssignmentEmployeesModal((prev) => {
          if (!prev || prev.kind !== kind || prev.id !== id) return prev;
          return { ...prev, employees: [], loading: false };
        });
      }
    },
    [employeesCache]
  );

  const fetchTasks = async () => {
    if (!employee?.id) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const tasksData = await taskApi.getMyTasks();
      const validTasks = Array.isArray(tasksData) ? tasksData.filter((t: any) => t?.id) : [];
      setTasks(validTasks);
    } catch (error: any) {
      console.error('Error fetching tasks:', error);
      setTasks([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, [employee?.id]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchTasks();
  };

  const openTaskDetail = async (task: MyTask) => {
    setSelectedTask(task);
    setTaskDetail(null);
    setConversations([]);
    setLoadError(null);
    setExpandedSubTaskId(null);
    setSubTaskDetailLoading({});
    setSubTaskDetails({});
    setSubTaskConversations({});
    setSubTaskNewMessage({});
    setLoadingDetail(true);
    const [taskResult, convResult] = await Promise.allSettled([
      taskApi.getById(task.id),
      taskApi.getConversations(task.id),
    ]);
    const fullTask = taskResult.status === 'fulfilled' ? taskResult.value : (task as Task);
    const convs = convResult.status === 'fulfilled' ? (convResult.value || []) : [];
    if (taskResult.status === 'rejected') {
      console.error('Error loading task:', taskResult.reason);
      setLoadError('Could not load full task details.');
    }
    if (convResult.status === 'rejected') {
      console.error('Error loading conversations:', convResult.reason);
      setLoadError(prev => (prev ? `${prev} ` : '') + 'Could not load conversations.');
    }
    if (task.parentTaskReadOnly && task.subTasks && task.subTasks.length > 0 && fullTask.subTasks) {
      const allowedIds = new Set(task.subTasks.map((st: { id: number }) => st.id));
      fullTask.subTasks = fullTask.subTasks.filter((st: { id: number }) => allowedIds.has(st.id));
    }
    setTaskDetail(fullTask);
    setConversations(convs);
    setLoadingDetail(false);
  };

  const closeTaskDetail = () => {
    setSelectedTask(null);
    setTaskDetail(null);
    setConversations([]);
    setLoadError(null);
    setExpandedSubTaskId(null);
    setSubTaskDetailLoading({});
    setSubTaskDetails({});
    setSubTaskConversations({});
  };

  const expandSubTask = async (subTaskId: number) => {
    if (!selectedTask) return;
    if (expandedSubTaskId === subTaskId) {
      setExpandedSubTaskId(null);
      return;
    }
    setExpandedSubTaskId(subTaskId);
    setSubTaskDetailLoading((prev) => ({ ...prev, [subTaskId]: true }));
    try {
      const [detail, convs] = await Promise.all([
        subTaskApi.getById(selectedTask.id, subTaskId),
        subTaskApi.getConversations(selectedTask.id, subTaskId),
      ]);
      setSubTaskDetails((prev) => ({ ...prev, [subTaskId]: detail }));
      setSubTaskConversations((prev) => ({ ...prev, [subTaskId]: convs }));
    } catch (e) {
      console.error('Error loading sub-task:', e);
      setExpandedSubTaskId(null);
    } finally {
      setSubTaskDetailLoading((prev) => ({ ...prev, [subTaskId]: false }));
    }
  };

  const sendSubTaskMessage = async (subTaskId: number) => {
    const msg = subTaskNewMessage[subTaskId]?.trim();
    if (!msg || !selectedTask || sendingSubTaskMessage[subTaskId]) return;
    setSendingSubTaskMessage(prev => ({ ...prev, [subTaskId]: true }));
    try {
      const formData = new FormData();
      formData.append('message', msg);
      const newConv = await subTaskApi.addConversation(selectedTask.id, subTaskId, formData);
      setSubTaskConversations(prev => ({
        ...prev,
        [subTaskId]: [...(prev[subTaskId] || []), newConv],
      }));
      setSubTaskNewMessage(prev => ({ ...prev, [subTaskId]: '' }));
    } catch (e) {
      console.error('Error sending message:', e);
    } finally {
      setSendingSubTaskMessage(prev => ({ ...prev, [subTaskId]: false }));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'InProgress':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      default:
        return 'bg-neutral-50 text-neutral-700 border-neutral-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Completed':
        return <CheckCircle className="h-4 w-4" />;
      case 'InProgress':
        return <Loader2 className="h-4 w-4 animate-spin" />;
      default:
        return <Circle className="h-4 w-4" />;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const isOverdue = (dueDate: string, status: string) => {
    if (status === 'Completed') return false;
    return new Date(dueDate) < new Date();
  };

  const getTimeProgress = (task: MyTask) => {
    const createdAt = (task as { createdAt?: string }).createdAt;
    if (!createdAt) return 0;
    const start = new Date(createdAt).getTime();
    const end = new Date(task.dueDate).getTime();
    const now = Date.now();
    if (end <= start) return 100;
    if (task.status === 'Completed') return 100;
    const total = end - start;
    const elapsed = Math.min(Math.max(now - start, 0), total);
    return Math.round((elapsed / total) * 100);
  };

  const getSubTaskProgress = (task: MyTask) => {
    const subs = task.subTasks || [];
    if (subs.length === 0) return { completed: 0, total: 0, percent: 0 };
    const completed = subs.filter((s) => s.status === 'Completed').length;
    return { completed, total: subs.length, percent: Math.round((completed / subs.length) * 100) };
  };

  const ProgressCircle: React.FC<{ percent: number; color: string; size?: number; title: string; label: string }> = ({ percent, color, size = 36, title, label }) => {
    const r = (size / 2) - 4;
    const circ = 2 * Math.PI * r;
    const filled = (percent / 100) * circ;
    return (
      <div className="relative flex flex-col items-center" title={title}>
        <svg width={size} height={size} className="flex-shrink-0">
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeWidth="4" className="text-neutral-200" />
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="4" strokeDasharray={`${filled} ${circ}`} strokeLinecap="round" transform={`rotate(-90 ${size / 2} ${size / 2})`} />
        </svg>
        <span className="text-[10px] font-medium text-neutral-600 mt-0.5">{label}</span>
      </div>
    );
  };

  const handleDelete = async (taskId: number) => {
    if (!(await appConfirm('Are you sure you want to delete this task?', { danger: true, confirmLabel: 'Delete' }))) return;
    try {
      await taskApi.delete(taskId);
      setTasks(prev => prev.filter(t => t.id !== taskId));
      if (selectedTask?.id === taskId) closeTaskDetail();
    } catch (error: any) {
      console.error('Error deleting task:', error);
      alert(error?.response?.data?.message || 'Failed to delete task.');
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-neutral-800">My Tasks</h1>
        <button
          onClick={onRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-neutral-500">Loading tasks...</div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Created By</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Assigned To</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Due Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Progress</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-neutral-200">
                {tasks.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-neutral-500">
                      No tasks assigned to you.
                    </td>
                  </tr>
                ) : (
                  tasks.map((task: MyTask) => {
                    const accessHint = myTaskAccessMeta(task);
                    return (
                    <tr key={task.id} className="hover:bg-neutral-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-neutral-900">{task.name}</div>
                        <span
                          className="inline-block mt-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-md bg-neutral-100 text-neutral-600 border border-neutral-200 cursor-help max-w-full"
                          title={accessHint.detail}
                        >
                          {accessHint.label}
                        </span>
                        {task.description && (
                          <div className="text-sm text-neutral-500 mt-1 line-clamp-2">{task.description}</div>
                        )}
                        {task.attachments && task.attachments.length > 0 && (
                          <div className="mt-2 space-y-1">
                            <div className="text-xs font-medium text-neutral-500">Attachments:</div>
                            {task.attachments.map((attachment: { id: number; fileName: string; filePath: string; fileSize?: number }) => (
                              <a
                                key={attachment.id}
                                href={getUploadedFileUrl(attachment.filePath)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                                title={attachment.fileName}
                              >
                                <FileIcon className="h-3 w-3 flex-shrink-0" />
                                <span className="truncate max-w-[200px]">{attachment.fileName}</span>
                                {attachment.fileSize != null && (
                                  <span className="text-neutral-500">({formatFileSize(attachment.fileSize)})</span>
                                )}
                              </a>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-neutral-900">
                          {task.createdBy?.firstName} {task.createdBy?.lastName}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          {task.assignedTo && task.assignedTo.length > 0 && (
                            <>
                              {task.assignedTo.map((emp: { id: number; firstName: string; lastName: string }) => {
                                const initials = `${emp.firstName?.[0] || ''}${emp.lastName?.[0] || ''}`.toUpperCase();
                                const fullName = `${emp.firstName} ${emp.lastName}`;
                                const empProfileUrl = getProfileImageUrl((emp as { profileImageUrl?: string; updatedAt?: string }).profileImageUrl, (emp as { updatedAt?: string }).updatedAt);
                                return (
                                  <div key={emp.id} className="relative group">
                                    {empProfileUrl ? (
                                      <img src={empProfileUrl} alt={fullName} className="w-8 h-8 rounded-full object-cover cursor-pointer hover:ring-2 hover:ring-blue-600 transition-colors" />
                                    ) : (
                                      <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-semibold cursor-pointer hover:bg-blue-600 transition-colors">
                                        {initials}
                                      </div>
                                    )}
                                    <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-10">
                                      <div className="bg-neutral-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap">{fullName}</div>
                                      <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-neutral-800 ml-2"></div>
                                    </div>
                                  </div>
                                );
                              })}
                            </>
                          )}
                          {resolveAssignedPositions(task, positions).map((position) => (
                            <AssignmentCircleLink
                              key={`pos-${position.id}`}
                              kind="position"
                              label={position.title}
                              size="md"
                              onClick={() => openAssignmentEmployeesModal('position', position.id, position.title, task.locationId)}
                            />
                          ))}
                          {resolveAssignedSchedules(task, schedules).map((schedule) => (
                            <AssignmentCircleLink
                              key={`sched-${schedule.id}`}
                              kind="schedule"
                              label={schedule.name}
                              size="md"
                              onClick={() => openAssignmentEmployeesModal('schedule', schedule.id, schedule.name, task.locationId)}
                            />
                          ))}
                          {(!task.assignedTo || task.assignedTo.length === 0) &&
                           resolveAssignedPositions(task, positions).length === 0 &&
                           resolveAssignedSchedules(task, schedules).length === 0 && (
                            <span className="text-xs text-neutral-400">Unassigned</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-neutral-400" />
                          <span className={`text-sm ${isOverdue(task.dueDate, task.status) ? 'text-red-600 font-semibold' : 'text-neutral-900'}`}>
                            {formatCompanyDate(task.dueDate)}
                          </span>
                          {isOverdue(task.dueDate, task.status) && (
                            <span className="text-xs text-red-600">(Overdue)</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(task.status)}`}>
                            {getStatusIcon(task.status)}
                            {task.status}
                          </span>
                          {task.status === 'Completed' && task.completedAt && (
                            <span className="text-xs text-neutral-500">
                              Completed: {formatCompanyDate(task.completedAt)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-4">
                          <ProgressCircle
                            percent={getTimeProgress(task)}
                            color="#3b82f6"
                            title={`Time: ${getTimeProgress(task)}% passed, ${100 - getTimeProgress(task)}% remaining`}
                            label={`${getTimeProgress(task)}% passed`}
                          />
                          {getSubTaskProgress(task).total > 0 ? (
                            <ProgressCircle
                              percent={getSubTaskProgress(task).percent}
                              color="#22c55e"
                              title={`Sub-tasks: ${getSubTaskProgress(task).completed} completed, ${getSubTaskProgress(task).total - getSubTaskProgress(task).completed} remaining`}
                              label={`${getSubTaskProgress(task).completed}/${getSubTaskProgress(task).total}`}
                            />
                          ) : (
                            <div className="flex flex-col items-center" title="No sub-tasks">
                              <div className="w-9 h-9 rounded-full border-2 border-dashed border-neutral-300 flex items-center justify-center text-neutral-400 text-xs">0</div>
                              <span className="text-[10px] font-medium text-neutral-500 mt-0.5">N/A</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openTaskDetail(task)}
                            className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50 relative"
                            title="View Sub-Tasks"
                          >
                            <GitBranch className="h-4 w-4" />
                            {task.subTasks && task.subTasks.length > 0 && (
                              <span className="absolute -top-2 -right-2 bg-green-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center border-2 border-white">
                                {task.subTasks.length}
                              </span>
                            )}
                          </button>
                          <button
                            onClick={() => openTaskDetail(task)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 relative"
                            title="View Conversations"
                          >
                            <MessageSquare className="h-4 w-4" />
                            {task.conversationsCount !== undefined && task.conversationsCount > 0 && (
                              <span className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center border-2 border-white">
                                {task.conversationsCount}
                              </span>
                            )}
                          </button>
                          <button
                            onClick={() => openTaskDetail(task)}
                            className="text-neutral-600 hover:text-neutral-900 p-1 rounded hover:bg-neutral-100"
                            title="View Task Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          {task.canEditTask && (
                            <>
                              <button
                                onClick={() => navigate('/task-admin', { state: { taskId: task.id, canEditTask: task.canEditTask, canEditSubTask: task.canEditSubTask, isLimitedTask: task.isLimitedTask, isLimitedSubTask: task.isLimitedSubTask } })}
                                className="text-neutral-600 hover:text-neutral-900 p-1 rounded hover:bg-neutral-100"
                                title="Edit Task"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              {hasSuperAdminRole && (
                                <button
                                  onClick={() => handleDelete(task.id)}
                                  className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                                  title="Delete Task (Super Admin only)"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );})
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Task Detail Modal */}
      {selectedTask && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={closeTaskDetail}>
          <div
            className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center border-b border-neutral-200 px-6 py-4 flex-shrink-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-semibold text-neutral-800">
                  {loadingDetail ? 'Loading...' : (taskDetail?.name || selectedTask.name)}
                </h2>
                {selectedTask?.parentTaskReadOnly && (
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                    Parent task read-only
                  </span>
                )}
                {selectedTask &&
                  (() => {
                    const h = myTaskAccessMeta(selectedTask);
                    return (
                      <span
                        className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded-md bg-slate-100 text-slate-600 border border-slate-200 cursor-help"
                        title={h.detail}
                      >
                        {h.label}
                      </span>
                    );
                  })()}
              </div>
              <div className="flex items-center gap-2">
                {hasTaskManagement && selectedTask && (
                  <button
                    onClick={() => {
                      closeTaskDetail();
                      navigate('/task-admin', {
                        state: {
                          taskId: selectedTask.id,
                          canEditTask: true,
                          canEditSubTask: true,
                          isLimitedTask: false,
                          isLimitedSubTask: false,
                        },
                      });
                    }}
                    className="flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Edit in Task Admin
                  </button>
                )}
                <button onClick={closeTaskDetail} className="p-2 hover:bg-neutral-100 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {loadingDetail ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : taskDetail ? (
                <>
                  {loadError && (
                    <div className="px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                      {loadError}
                    </div>
                  )}
                  {taskDetail.description && (
                    <div>
                      <h3 className="text-sm font-medium text-neutral-600 mb-1">Description</h3>
                      <p className="text-neutral-800">{taskDetail.description}</p>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(taskDetail.status)}`}>
                      {taskDetail.status}
                    </span>
                    <span className="text-sm text-neutral-600">
                      Due: {formatCompanyDate(taskDetail.dueDate)}
                    </span>
                    {taskDetail.status === 'Completed' && taskDetail.completedAt && (
                      <span className="text-sm text-neutral-600">
                        Completed: {formatCompanyDate(taskDetail.completedAt)}
                      </span>
                    )}
                    {taskDetail.createdBy && (
                      <span className="text-sm text-neutral-600">
                        Created by: {taskDetail.createdBy.firstName} {taskDetail.createdBy.lastName}
                      </span>
                    )}
                  </div>
                  {((taskDetail as Task).assignedTo?.length ||
                    resolveAssignedPositions(taskDetail as Task, positions).length > 0 ||
                    resolveAssignedSchedules(taskDetail as Task, schedules).length > 0) ? (
                    <div>
                      <h3 className="text-sm font-medium text-neutral-600 mb-1">Assigned To</h3>
                      <div className="flex flex-wrap gap-2 items-center">
                        {(taskDetail as Task).assignedTo?.map((emp: { id: number; firstName: string; lastName: string }) => (
                          <span key={emp.id} className="px-2 py-1 bg-blue-50 rounded text-xs">
                            {emp.firstName} {emp.lastName}
                          </span>
                        ))}
                        {resolveAssignedPositions(taskDetail as Task, positions).map((p) => (
                          <AssignmentCircleLink
                            key={p.id}
                            kind="position"
                            label={p.title}
                            size="md"
                            onClick={() =>
                              openAssignmentEmployeesModal('position', p.id, p.title, (taskDetail as Task).locationId)
                            }
                          />
                        ))}
                        {resolveAssignedSchedules(taskDetail as Task, schedules).map((s) => (
                          <AssignmentCircleLink
                            key={s.id}
                            kind="schedule"
                            label={s.name}
                            size="md"
                            onClick={() =>
                              openAssignmentEmployeesModal('schedule', s.id, s.name, (taskDetail as Task).locationId)
                            }
                          />
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {(taskDetail as Task).attachments?.length ? (
                    <div>
                      <h3 className="text-sm font-medium text-neutral-600 mb-1">Attachments</h3>
                      <div className="space-y-1">
                        {(taskDetail as Task).attachments?.map((a: { id: number; fileName: string; filePath: string }) => (
                          <a key={a.id} href={getUploadedFileUrl(a.filePath)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 bg-neutral-50 rounded hover:bg-neutral-100">
                            <FileIcon className="h-4 w-4" />
                            <span className="text-sm truncate">{a.fileName}</span>
                            <Download className="h-3 w-3 text-neutral-400" />
                          </a>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {taskDetail.subTasks && taskDetail.subTasks.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-neutral-600 mb-2">SubTasks</h3>
                      <div className="space-y-2">
                        {taskDetail.subTasks.map((st: { id: number; name: string; description?: string; status: string; dueDate: string }) => (
                          <div key={st.id} className="rounded-lg border border-neutral-200 overflow-hidden">
                            <div
                              className="p-3 bg-neutral-50 flex justify-between items-center cursor-pointer hover:bg-neutral-100"
                              onClick={() => expandSubTask(st.id)}
                            >
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                {expandedSubTaskId === st.id ? <ChevronUp className="h-4 w-4 flex-shrink-0 text-neutral-600" /> : <ChevronDown className="h-4 w-4 flex-shrink-0 text-neutral-600" />}
                                <div className="min-w-0">
                                  <span className="font-medium">{st.name}</span>
                                  {expandedSubTaskId !== st.id && !selectedTask?.canEditSubTask && (
                                    <>
                                      {st.description && <p className="text-sm text-neutral-600 mt-0.5">{st.description}</p>}
                                      <p className="text-xs text-neutral-500 mt-0.5">Due: {formatCompanyDate(st.dueDate)}</p>
                                    </>
                                  )}
                                </div>
                                <span className={`px-2 py-0.5 text-xs rounded-full flex-shrink-0 ${getStatusColor(st.status)}`}>
                                  {st.status}
                                </span>
                              </div>
                              {selectedTask && canEditSubTaskRowInMyTasks(selectedTask, st.id) && (
                                <button
                                  onClick={e => {
                                    e.stopPropagation();
                                    closeTaskDetail();
                                    navigate('/task-admin', {
                                      state: {
                                        taskId: selectedTask.id,
                                        canEditTask: selectedTask.canEditTask,
                                        canEditSubTask: selectedTask.canEditSubTask,
                                        isLimitedTask: selectedTask.isLimitedTask,
                                        isLimitedSubTask: selectedTask.isLimitedSubTask,
                                      },
                                    });
                                  }}
                                  className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 flex-shrink-0"
                                >
                                  Edit
                                </button>
                              )}
                            </div>
                            {expandedSubTaskId === st.id && (
                              <div className="p-4 border-t border-neutral-200 space-y-3">
                                {subTaskDetailLoading[st.id] && !subTaskDetails[st.id] ? (
                                  <div className="flex items-center gap-2 text-sm text-neutral-500">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Loading sub-task…
                                  </div>
                                ) : subTaskDetails[st.id] ? (
                                  <>
                                    {subTaskDetails[st.id].description && <p className="text-sm text-neutral-600">{subTaskDetails[st.id].description}</p>}
                                    <p className="text-xs text-neutral-500">Due: {formatCompanyDate(subTaskDetails[st.id].dueDate)}</p>
                                    {((subTaskDetails[st.id].assignedTo?.length ?? 0) > 0 ||
                                      resolveAssignedPositions(subTaskDetails[st.id], positions).length > 0 ||
                                      resolveAssignedSchedules(subTaskDetails[st.id], schedules).length > 0) && (
                                      <div>
                                        <h4 className="text-xs font-medium text-neutral-600 mb-2">Assigned to</h4>
                                        <div className="flex flex-wrap gap-2 items-center">
                                          {(subTaskDetails[st.id].assignedTo ?? []).map((emp: { id: number; firstName: string; lastName: string; profileImageUrl?: string; updatedAt?: string }) => {
                                            const fullName = `${emp.firstName} ${emp.lastName}`;
                                            const empProfileUrl = getProfileImageUrl(emp.profileImageUrl, emp.updatedAt);
                                            return (
                                              <div key={emp.id} className="relative group">
                                                {empProfileUrl ? (
                                                  <img src={empProfileUrl} alt={fullName} className="w-8 h-8 rounded-full object-cover border border-neutral-200" title={fullName} />
                                                ) : (
                                                  <div
                                                    className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-semibold"
                                                    title={fullName}
                                                  >
                                                    {`${emp.firstName?.[0] || ''}${emp.lastName?.[0] || ''}`.toUpperCase()}
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })}
                                          {resolveAssignedPositions(subTaskDetails[st.id], positions).map((p) => (
                                            <AssignmentCircleLink
                                              key={`st-pos-${st.id}-${p.id}`}
                                              kind="position"
                                              label={p.title}
                                              size="md"
                                              onClick={() =>
                                                openAssignmentEmployeesModal('position', p.id, p.title, (taskDetail as Task).locationId)
                                              }
                                            />
                                          ))}
                                          {resolveAssignedSchedules(subTaskDetails[st.id], schedules).map((s) => (
                                            <AssignmentCircleLink
                                              key={`st-sched-${st.id}-${s.id}`}
                                              kind="schedule"
                                              label={s.name}
                                              size="md"
                                              onClick={() =>
                                                openAssignmentEmployeesModal('schedule', s.id, s.name, (taskDetail as Task).locationId)
                                              }
                                            />
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    <div>
                                      <h4 className="text-xs font-medium text-neutral-600 mb-2">Chat</h4>
                                      <div className="space-y-2 max-h-32 overflow-y-auto mb-2">
                                        {(subTaskConversations[st.id] || []).map((c: SubTaskConversation) => (
                                          <div key={c.id} className="p-2 bg-neutral-50 rounded flex gap-2">
                                            <div className="flex-1 min-w-0">
                                              <span className="text-xs font-medium">{c.employee?.firstName} {c.employee?.lastName}: </span>
                                              <span className="text-sm">{c.message}</span>
                                            </div>
                                            <span className="text-xs text-neutral-500">{c.createdAt ? format(parseISO(c.createdAt), 'HH:mm') : ''}</span>
                                          </div>
                                        ))}
                                      </div>
                                      {selectedTask && canChatOnSubTaskInMyTasks(selectedTask, st.id) && (
                                        <div className="flex gap-2">
                                          <input
                                            type="text"
                                            placeholder="Type a message..."
                                            value={subTaskNewMessage[st.id] || ''}
                                            onChange={e => setSubTaskNewMessage(prev => ({ ...prev, [st.id]: e.target.value }))}
                                            onKeyDown={e => e.key === 'Enter' && sendSubTaskMessage(st.id)}
                                            className="flex-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                                          />
                                          <button
                                            onClick={() => sendSubTaskMessage(st.id)}
                                            disabled={sendingSubTaskMessage[st.id] || !(subTaskNewMessage[st.id]?.trim())}
                                            className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                          >
                                            <Send className="h-4 w-4" />
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  </>
                                ) : (
                                  <p className="text-sm text-neutral-500">Could not load this sub-task.</p>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <h3 className="text-sm font-medium text-neutral-600 mb-2">Conversations ({conversations.length})</h3>
                    {conversations.length === 0 ? (
                      <p className="text-sm text-neutral-500">No conversations yet</p>
                    ) : (
                      <div className="space-y-3 max-h-48 overflow-y-auto">
                        {conversations.map(conv => {
                          const emp = conv.employee;
                          const empProfileUrl = emp && getProfileImageUrl((emp as { profileImageUrl?: string }).profileImageUrl);
                          return (
                          <div key={conv.id} className="p-3 bg-neutral-50 rounded-lg border border-neutral-200 flex gap-3">
                            {empProfileUrl ? (
                              <img src={empProfileUrl} alt={`${emp?.firstName} ${emp?.lastName}`} className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                                {emp?.firstName?.[0] || emp?.email?.[0] || 'U'}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between text-xs text-neutral-500 mb-1">
                                <span>{emp?.firstName} {emp?.lastName}</span>
                                <span>{conv.createdAt ? formatCompanyDateTime(conv.createdAt) : 'N/A'}</span>
                              </div>
                              <p className="text-sm text-neutral-800">{conv.message}</p>
                            </div>
                          </div>
                        );})}
                      </div>
                    )}
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {assignmentEmployeesModal && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
          role="presentation"
          onClick={() => setAssignmentEmployeesModal(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[min(85vh,520px)] overflow-hidden flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mytasks-assignment-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-neutral-200 flex justify-between items-start gap-2">
              <div>
                <h3 id="mytasks-assignment-modal-title" className="text-lg font-semibold text-neutral-900">
                  {assignmentEmployeesModal.kind === 'position' ? 'Position' : 'Schedule'}:{' '}
                  {assignmentEmployeesModal.label}
                </h3>
                <p className="text-xs text-neutral-500 mt-1">
                  Active employees who match this {assignmentEmployeesModal.kind} on their profile
                  {assignmentEmployeesModal.loading ? '' : ` (${assignmentEmployeesModal.employees.length})`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setAssignmentEmployeesModal(null)}
                className="p-1 rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 shrink-0"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-4 py-3 overflow-y-auto flex-1 min-h-0">
              {assignmentEmployeesModal.loading ? (
                <div className="flex flex-col items-center justify-center py-12 gap-2 text-neutral-500">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                  <span className="text-sm">Loading employees…</span>
                </div>
              ) : assignmentEmployeesModal.employees.length === 0 ? (
                <p className="text-sm text-neutral-600 py-4">
                  No active employees found for this assignment. If the task has a location, only employees linked to
                  that location are listed.
                </p>
              ) : (
                <ul className="divide-y divide-neutral-100">
                  {assignmentEmployeesModal.employees.map((emp) => {
                    const url = getProfileImageUrl(emp.profileImageUrl, emp.updatedAt);
                    const initials = `${emp.firstName?.[0] || ''}${emp.lastName?.[0] || ''}`.toUpperCase();
                    return (
                      <li key={emp.id} className="flex items-center gap-3 py-2.5">
                        {url ? (
                          <img src={url} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-semibold shrink-0">
                            {initials}
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-neutral-900 truncate">
                            {emp.firstName} {emp.lastName}
                          </div>
                          {emp.email && <div className="text-xs text-neutral-500 truncate">{emp.email}</div>}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="px-4 py-3 border-t border-neutral-200 flex justify-end">
              <button
                type="button"
                onClick={() => setAssignmentEmployeesModal(null)}
                className="px-4 py-2 text-sm font-medium text-neutral-700 bg-neutral-100 rounded-md hover:bg-neutral-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyTasks;
